import React, { Dispatch, ReactNode, SetStateAction } from 'react';
import { useNoAuthHooks } from '../../../../../../../../Hooks/hooks';
import { formatPercent } from '../../../../../../../../Utilits/utilits';
import useMainCartDialog from './mainCartDialog-hook';
import { ICartItem, IDineInLocation, ItemCounterTypes } from '../../../../../../../../Interfaces/componentTypes';
import { VisitorPageContents } from '../../../../../visitorPage';
import { IBusinessProfile, IServices } from '../../../../../../../../Interfaces/responseTypes';
import ItemCounter from '../../ItemCounter/itemCounter';
import useLanguagesHooks from '../../../../../../../../Components/Languages/setLanguage-hooks';
import { DeliveryIconComponent, DineinIcon, PlateIcon } from '../../../../../../../../Components/IconComponents';
import PackageIcon from '../../../../../../../../Components/IconComponents/packageIcon';
import { SmallTrash } from '../../../../../../../../Assets/Icons';
import { EmptyCart } from '../../../../../../../../Assets/Images';
import BusinessClosedNotice from '../../BusinessClosedNotice/businessClosedNotice';
import { isBusinessClosed } from '../../../../../../../../Utilits/utilits';
import styles from './mainCartDialog.module.scss';

export interface IMainCartDialogProps {
    companyId: number,
    company_name: string,
    company_logo: string,
    company_contacts?: string[],
    start_time?: string | null,
    end_time?: string | null,
    business_profile?: IBusinessProfile,
    cart: [] | ICartItem[];
    setMainCart: Dispatch<SetStateAction<[] | ICartItem[]>>,
    currencySymbol: string,
    serviceFee?: number,
    pos_terminal?: boolean | null,
    services: null | IServices,
    setDialog: Dispatch<SetStateAction<ReactNode | null>>;
    deliveryMinCost: number | null | undefined,
    deliveryCities?: IServices['delivery']['cities'],
    onCloseDialog: () => void;
    setContentType: Dispatch<SetStateAction<VisitorPageContents>>,
    dineInLocation?: IDineInLocation | null,
    dineInLocationFromUrl?: IDineInLocation | null,
};

type DineInButtonVariant = 'split' | 'stacked' | 'solo' | 'legacy-split' | 'legacy-solo';

interface IDineInCartButtonProps {
    variant: DineInButtonVariant;
    clickable: boolean;
    locationLabel: string | null;
    serviceFee?: number;
    dineInPrice: number;
    currencySymbol: string;
    onClick?: () => void;
}

const DineInCartButton: React.FC<IDineInCartButtonProps> = ({
    variant,
    clickable,
    locationLabel,
    serviceFee,
    dineInPrice,
    currencySymbol,
    onClick,
}) => {
    const { t } = useLanguagesHooks();
    const showServiceFee = typeof serviceFee === 'number' && serviceFee > 0;
    const isSplitAction = variant === 'split' && clickable;
    const isStackedAction = variant === 'stacked' && clickable;
    const isSoloAction = variant === 'solo' && clickable;
    const dineInButtonLabel = !clickable
        ? t('Visitor.Dine_In')
        : isSoloAction
            ? t('Visitor.Dine_In_Order.Cart_Button_Solo')
            : t('Visitor.Dine_In_Order.Cart_Button');

    const buttonClassName = [
        styles.dineInCartButton,
        styles[`dineInCartButton_${variant}`],
        clickable ? styles.dineInButton : styles.dineInCartButtonStatic,
        isSplitAction ? styles.dineInCartButton_splitAction : '',
        isStackedAction ? styles.dineInCartButton_stackedAction : '',
        isSoloAction ? styles.dineInCartButton_soloAction : '',
    ].join(' ');

    const buttonProps = {
        className: buttonClassName,
        onClick: clickable ? onClick : undefined,
        role: clickable ? 'button' as const : undefined,
        tabIndex: clickable ? 0 : undefined,
        onKeyDown: clickable ? (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick?.();
            }
        } : undefined,
    };

    if (variant === 'solo' && clickable) {
        return (
            <div {...buttonProps}>
                <div className={styles.dineInSoloTop}>
                    <div className={styles.dineInSoloMain}>
                        <DineinIcon color='white' />
                        <div className={styles.dineInSoloText}>
                            <p>{dineInButtonLabel}</p>
                            {(locationLabel || showServiceFee) && (
                                <span className={styles.dineInSoloMeta}>
                                    {locationLabel}
                                    {locationLabel && showServiceFee && (
                                        <span className={styles.dineInStackedDot}> · </span>
                                    )}
                                    {showServiceFee && (
                                        `${t('Visitor.Service_Fee')} ${formatPercent(String(serviceFee))}%`
                                    )}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className={styles.dineInSoloTotal}>
                        <span>{`${parseInt(String(dineInPrice))} ${currencySymbol}`}</span>
                    </div>
                </div>
            </div>
        );
    }

    if (variant === 'stacked' && clickable) {
        return (
            <div {...buttonProps}>
                <div className={styles.dineInStackedTop}>
                    <div className={styles.dineInStackedTitle}>
                       <DineinIcon color='white' />
                        <p>{dineInButtonLabel}</p>
                    </div>
                    <div className={styles.dineInStackedTotal}>
                        <span>{`${parseInt(String(dineInPrice))} ${currencySymbol}`}</span>
                    </div>
                </div>
                {(locationLabel || showServiceFee) && (
                    <div className={styles.dineInStackedMeta}>
                        {locationLabel && <span>{locationLabel}</span>}
                        {locationLabel && showServiceFee && <span className={styles.dineInStackedDot}>·</span>}
                        {showServiceFee && (
                            <span>{`${t('Visitor.Service_Fee')} ${formatPercent(String(serviceFee))}%`}</span>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div {...buttonProps}>
            <div className={styles.dineInCartHeader}>
                <PlateIcon className={styles.plateIcon} />
                <p>{dineInButtonLabel}</p>
            </div>
            {variant !== 'split' && locationLabel && (
                <p className={styles.locationHint}>{locationLabel}</p>
            )}
            {showServiceFee && (
                <div className={styles.serviceFee}>
                    {`${t('Visitor.Service_Fee')} ${formatPercent(String(serviceFee))}%`}
                </div>
            )}
            <div className={styles.total}>
                <p>{t('Visitor.Total')}</p>
                <span>{`${parseInt(String(dineInPrice))} ${currencySymbol}`}</span>
            </div>
        </div>
    );
};

const MainCartDialog: React.FC<IMainCartDialogProps> = (props) => {
    const { currencySymbol, serviceFee, services, setMainCart, business_profile, dineInLocationFromUrl } = props;
    const { baseURL, renderOptions } = useNoAuthHooks();
    const { t, activeLanguage } = useLanguagesHooks();
    const isClosed = isBusinessClosed(business_profile);
    const {
        dialogCart,
        dineInPrice,
        showEmptyCart,
        isDineInActive,
        isAnyActiveService,
        canOrderDineIn,
        hasDelivery,
        hasPreOrder,
        useSplitDineInFooter,
        useStackedDineInFooter,
        useDineInOnlyFooter,
        setDialogCart,
        onOpenPreOrderDialog,
        removeFromDialogCart,
        onOpenOrderDeliveryDialog,
        onOpenDineInOrderDialog,
    } = useMainCartDialog(props);

    const locationLabel = dineInLocationFromUrl
        ? `${t(`My_Business.My_Services.Dine_In.Unit_Types.${dineInLocationFromUrl.location_type}`)} #${dineInLocationFromUrl.location_number}`
        : null;

    const dineInButtonProps = {
        locationLabel,
        serviceFee,
        dineInPrice,
        currencySymbol,
        onClick: onOpenDineInOrderDialog,
    };

    const renderDeliveryButton = (fullWidth = false) => (
        hasDelivery && (
            <div
                className={`${styles.deliveryButton} ${fullWidth ? styles.fullWidthButton : ''}`}
                onClick={onOpenOrderDeliveryDialog}
            >
                <DeliveryIconComponent className={styles.buttonIcon} />
                <p>{t('Visitor.Delivery_Button')}</p>
            </div>
        )
    );

    const renderPreOrderButton = (fullWidth = false) => (
        hasPreOrder && (
            <div
                className={`${styles.preOrderButton} ${fullWidth ? styles.fullWidthButton : ''}`}
                onClick={onOpenPreOrderDialog}
            >
                <PackageIcon className={styles.buttonIcon} color="white" />
                <p>{t('Visitor.Pre_Order_Button')}</p>
            </div>
        )
    );

    const renderServiceButtons = (fullWidth = false) => (
        <>
            {renderDeliveryButton(fullWidth)}
            {renderPreOrderButton(fullWidth)}
        </>
    );

    const renderCartFooter = () => {
        if (dialogCart.length === 0) {
            return null;
        }

        if (isDineInActive) {
            if (canOrderDineIn) {
                if (useSplitDineInFooter) {
                    return (
                        <div className={styles.buttonFooter}>
                            <DineInCartButton
                                {...dineInButtonProps}
                                variant="split"
                                clickable
                            />
                            <div className={styles.serviceButtons}>
                                {renderServiceButtons()}
                            </div>
                        </div>
                    );
                }

                if (useStackedDineInFooter) {
                    return (
                        <div className={`${styles.buttonFooter} ${styles.stackedFooter}`}>
                            {renderDeliveryButton(true)}
                            {renderPreOrderButton(true)}
                            <DineInCartButton
                                {...dineInButtonProps}
                                variant="stacked"
                                clickable
                            />
                        </div>
                    );
                }

                if (useDineInOnlyFooter) {
                    return (
                        <div className={`${styles.bottom} ${styles.dineInSoloFooter}`}>
                            <DineInCartButton
                                {...dineInButtonProps}
                                variant="solo"
                                clickable
                            />
                        </div>
                    );
                }
            }

            if (isAnyActiveService) {
                return (
                    <div className={styles.buttonFooter}>
                        <DineInCartButton
                            {...dineInButtonProps}
                            variant="legacy-split"
                            clickable={false}
                        />
                        <div className={styles.serviceButtons}>
                            {renderServiceButtons()}
                        </div>
                    </div>
                );
            }

            return (
                <div className={styles.bottom}>
                    <DineInCartButton
                        {...dineInButtonProps}
                        variant="legacy-solo"
                        clickable={false}
                    />
                </div>
            );
        }

        if (isAnyActiveService) {
            return (
                <div className={styles.buttonFooter}>
                    <DineInCartButton
                        {...dineInButtonProps}
                        variant="legacy-split"
                        clickable={false}
                    />
                    <div className={styles.serviceButtons}>
                        {renderServiceButtons()}
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.bottom}>
                <DineInCartButton
                    {...dineInButtonProps}
                    variant="legacy-solo"
                    clickable={false}
                />
            </div>
        );
    };

    const handleOptionCountChange = (itemId: number, optionId: number, countChange: number) => {
        setDialogCart((prevCart) => {
            const updatedCart = prevCart.map((cartItem) => {
                if (cartItem.id === itemId) {
                    return {
                        ...cartItem,
                        option: cartItem.option.map((opt) =>
                            opt.option_id === optionId
                                ? { ...opt, count: opt.count + countChange }
                                : opt
                        ),
                    };
                }
                return cartItem;
            });
            setMainCart(updatedCart);
            return updatedCart;
        });
    };

    return (
        <div className={styles.wrapper}>
            {isClosed && business_profile && (
                <BusinessClosedNotice profile={business_profile} variant="cart" />
            )}
            <div className={styles.container}>
                <div
                    className={`${styles.items}`}
                    style={dialogCart.length === 0 ? { justifyContent: "center", alignItems: "center" } : {}}
                >
                    {dialogCart.length > 0 ? (
                        dialogCart.map((item) => {
                            const localizedTitle = (item[`name_${activeLanguage}` as keyof ICartItem] as string) || item.name_en;
                            return (
                                <div className={styles.item} key={item.id}>
                                    <div className={styles.itemHeader}>
                                        <div className={styles.titleBox}>
                                            <img src={`${baseURL}${item.image}`} className={styles.image} alt='image.png' />
                                            <p className={styles.title}>{localizedTitle}</p>
                                        </div>
                                        <div className={styles.deleteIcon} onClick={() => removeFromDialogCart(item.id)}>
                                            <img src={SmallTrash} alt='trash.svg' />
                                        </div>
                                    </div>
                                    <div className={styles.options}>
                                        {item.option.map((opt) => (
                                            <div className={styles.option} key={opt.option_id}>
                                                <p className={styles.portion}>{renderOptions(opt)}</p>
                                                <div className={styles.price}>
                                                    <p>{parseInt(opt.price)}</p>
                                                    <p>{currencySymbol}</p>
                                                </div>
                                                <ItemCounter
                                                    count={opt.count}
                                                    option={opt}
                                                    type={ItemCounterTypes.MAIN_CART}
                                                    handleAddOption={(_, c) =>
                                                        handleOptionCountChange(item.id, opt.option_id, c)
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        showEmptyCart && (
                            <div className={styles.emptyCart}>
                                <div className={styles.imageBox}>
                                    <img src={EmptyCart} alt='emptyCart.png' />
                                </div>
                                <p>{t('Visitor.Empty_Cart')}</p>
                            </div>
                        )
                    )}
                </div>
            </div>

            {renderCartFooter()}
        </div>
    );
};

export default MainCartDialog;
